import React, { useState, useEffect } from 'react';
import {Card, Typography, Upload, Button, Space, List, Alert, message, Form, Tag, Tooltip, Divider} from 'antd';
import { UploadOutlined, FileOutlined, DeleteOutlined, EyeOutlined, SwapOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import {
  useFindByDomainAndDomainId
} from '@/shared/api/client/file-upload-controller';
import {ActionFlowRequestDTO, AppapproDTO} from "@/shared/api/model";
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';

interface DocumentUploadViewProps {
  data?: ActionFlowRequestDTO;
  formikProps?: any;
  documentType?: string;
  required?: boolean;
  showExistingAttachments?: boolean; // Show existing attachments from valueData.attachments
  allowReplace?: boolean; // Allow replacing existing documents
}

/**
 * Component for uploading documents with specific document types
 * Used in the MEMBER_DEDUCTION_FORM node
 */
const DocumentUploadView: React.FC<DocumentUploadViewProps> = ({ 
  data, 
  formikProps,
  documentType = 'DEDUCTION_FORM',
  required = false,
  showExistingAttachments = true,
  allowReplace = true
}) => {
  const [form] = Form.useForm();
  const { Title, Text } = Typography;
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]); // Attachments from valueData
  const [replacedDocuments, setReplacedDocuments] = useState<Set<string>>(new Set()); // Track replaced documents
  const [replacingDocument, setReplacingDocument] = useState<string | null>(null); // Currently replacing document role


  // Extract request ID and loan ID from the data context
  const values = formikProps?.values || data || {};
  const [valueData, setValueData] = useState<AppapproDTO>(values.valueData as AppapproDTO);


  const requestId = data?.recordId || '';
  const branch = data?.valueData?.branch || '';
  const loanId = data?.valueData?.loanId || '';
  console.log('data', data);


  useEffect(() => {
    if (values.valueData) {
      setValueData(values.valueData as AppapproDTO);
      console.log('values.valueData', values.valueData);

      // Extract existing attachments from valueData for display
      if (showExistingAttachments && values.valueData.attachments) {
        const attachments = Array.isArray(values.valueData.attachments) 
          ? values.valueData.attachments 
          : [];
        setExistingAttachments(attachments);
        console.log('Existing attachments from valueData:', attachments);
      }
    }
  }, [values.valueData, form, showExistingAttachments]);
  
  // Use the file-upload-controller to fetch documents by category (loan ID)
  const { data: documentsResponse, isLoading } = useFindByDomainAndDomainId(
      'LOAN_APPLICATION',
      branch+'.'+loanId,
      // @ts-ignore
      {enabled: !!loanId  }
  );
  

  
  // Filter documents by document type
  useEffect(() => {
    if (documentsResponse?.data && Array.isArray(documentsResponse.data)) {
      // Filter documents by type
      const filteredDocs = documentsResponse.data.filter(
        (doc: any) => doc.documentType === documentType
      );
      setExistingDocuments(filteredDocs);
      
      // Update formik field if documents exist and it's required
      if (filteredDocs.length > 0 && required && formikProps?.setFieldValue) {
        formikProps.setFieldValue('hasRequiredDocuments', true);
        
        // Update valueData.attachments with the document information
        updateFormikAttachments(filteredDocs);
      }
    }
  }, [documentsResponse, documentType, required, formikProps]);
  
  // Function to update Formik's valueData.attachments field
  const updateFormikAttachments = (documents: any[]) => {
    if (!formikProps?.setFieldValue) return;
    
    // Get current attachments array or initialize if it doesn't exist
    const currentAttachments = formikProps.values.valueData?.attachments || [];
    
    // Create attachment objects from documents
    const attachmentsToAdd = documents.map(doc => ({
      id: doc.id,
      fileName: doc.fileName,
      documentType: doc.documentType,
      uploadDate: doc.uploadDate,
      fileSize: doc.fileSize,
      contentType: doc.contentType,
      url: `/api/files/preview/documents/${doc.id}`
    }));
    
    // Filter out any existing attachments with the same IDs to avoid duplicates
    const existingIds = new Set(attachmentsToAdd.map(a => a.id));
    const filteredAttachments = currentAttachments.filter((a: any) => 
      !existingIds.has(a.id) || a.documentType !== documentType
    );
    
    // Combine existing attachments with new ones
    const updatedAttachments = [...filteredAttachments, ...attachmentsToAdd];
    
    // Update the Formik valueData.attachments field
    formikProps.setFieldValue('valueData.attachments', updatedAttachments);
    console.log('Updated attachments in Formik:', updatedAttachments);
  };
  
  // Handle document view
  const handleViewDocument = (fileId: string) => {
    if (fileId) {
      window.open(`/api/files/preview/documents/${fileId}`, '_blank');
    }
  };
  
  // Handle document delete
  const handleDeleteDocument = async (documentId: string) => {
    try {
      // You would need to implement this API endpoint
      // const response = await deleteDocument(documentId);
      message.success('Document deleted successfully');
      
      // Remove the document from Formik's valueData.attachments
      if (formikProps?.setFieldValue && formikProps.values.valueData?.attachments) {
        const currentAttachments = formikProps.values.valueData.attachments;
        const updatedAttachments = currentAttachments.filter((a: any) => a.id !== documentId);
        formikProps.setFieldValue('valueData.attachments', updatedAttachments);
        console.log('Updated attachments after deletion:', updatedAttachments);
      }
      
      // @ts-ignore
      //refetch(); // Refresh the documents list
    } catch (error) {
      message.error('Failed to delete document');
    }
  };
  
  // Handle replacing an existing document
  const handleReplaceDocument = (attachment: any) => {
    setReplacingDocument(attachment.role || attachment.id);
    // Clear the file list to prepare for new upload
    setFileList([]);
  };

  // Cancel replacement
  const handleCancelReplace = () => {
    setReplacingDocument(null);
    setFileList([]);
  };

  // Custom upload handler
  const customUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    
    setUploading(true);
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Determine the role - use replacing document role if replacing, otherwise default
      const uploadRole = replacingDocument || 'signed_deduction_form';
      
      // Add metadata
      const metadata: any = {
        category: uploadRole,
        domain: 'LOAN_APPLICATION',
        domainId: data?.recordId,
        role: uploadRole,
        documentType: documentType,
        description: `${documentType} for loan ${loanId}`,
        requestId: requestId
      };
      
      // If replacing, add metadata to indicate this is a replacement
      if (replacingDocument) {
        metadata.isReplacement = true;
        metadata.replacedRole = replacingDocument;
        metadata.replacementDate = new Date().toISOString();
      }
      
      // Convert metadata to JSON string and append to form data
      formData.append('metadata', JSON.stringify(metadata));
      
      const response = await AXIOS_INSTANCE.post('/api/files/upload', formData);
      
      onSuccess(response, file);
      message.success(`${file.name} uploaded successfully`);
      
      // Update formik value if available and required
      if (required && formikProps?.setFieldValue) {
        formikProps.setFieldValue('hasRequiredDocuments', true);
      }
      console.log('File uploaded successfully:', formikProps, response);

      // Add the uploaded document to Formik's valueData.attachments
      if (response && formikProps?.setFieldValue) {
        const uploadRole = replacingDocument || 'signed_deduction_form';
        const newAttachment = {
          id: response.data?.id,
          fileName: file.name,
          label: replacingDocument ? `Replaced: ${replacingDocument}` : 'Deduction Form',
          role: uploadRole,
          documentType: documentType,
          uploadDate: new Date().toISOString(),
          fileSize: file.size,
          contentType: file.type,
          url: `/api/files/preview/documents/${response.data?.id}`,
          isReplacement: !!replacingDocument,
          replacedDocumentRole: replacingDocument || undefined
        };
        
        // Get current attachments array or initialize if it doesn't exist
        const currentAttachments = formikProps.values.valueData?.attachments || [];
        
        // If replacing, filter out the old attachment with the same role and mark it as replaced
        let updatedAttachments;
        if (replacingDocument) {
          // Mark the old document as replaced (keep it for reference but add replaced flag)
          updatedAttachments = currentAttachments.map((att: any) => {
            if (att.role === replacingDocument || att.id === replacingDocument) {
              return { ...att, isReplaced: true, replacedAt: new Date().toISOString(), replacedBy: response.data?.id };
            }
            return att;
          });
          // Add the new attachment
          updatedAttachments.push(newAttachment);
          
          // Track that this document was replaced
          setReplacedDocuments(prev => new Set(prev).add(replacingDocument));
          setReplacingDocument(null);
        } else {
          // Add the new attachment
          updatedAttachments = [...currentAttachments, newAttachment];
        }
        
        // Update the Formik valueData.attachments field
        formikProps.setFieldValue('valueData.attachments', updatedAttachments);
        console.log('Updated attachments in Formik after upload:', updatedAttachments);
      }

      // Refetch documents to get the updated list
      // refetch();
    } catch (error) {
      console.error('Upload error:', error);
      onError(error);
      message.error(`${file.name} upload failed.`);
    } finally {
      setUploading(false);
    }
  };
  
  // Upload props
  const uploadProps: UploadProps = {
    name: 'file',
    customRequest: customUpload,
    fileList,
    onChange(info) {
      let newFileList = [...info.fileList];
      
      // Limit to the last uploaded file
      newFileList = newFileList.slice(-1);
      
      // Update fileList state
      setFileList(newFileList);
    },
  };
  
  // Get document type display name
  const getDocumentTypeDisplay = (type: string) => {
    return type.replace(/_/g, ' ');
  };
  
  return (
    <Card title={`Upload ${getDocumentTypeDisplay(documentType)}`} style={{ marginBottom: 16 }}>
      {required && (
        <Alert
          message="Required Document"
          description={`A ${getDocumentTypeDisplay(documentType)} is required to proceed with this application.`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      
      <div style={{ marginBottom: 16 }}>
        <Text>
          Please upload the signed document. Accepted file formats are PDF, JPG, and PNG.
        </Text>
      </div>
      
      <Upload {...uploadProps}>
        <Button icon={<UploadOutlined />} loading={uploading}>
          Select File
        </Button>
      </Upload>
      
      {existingDocuments.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>Uploaded Documents</Title>
          <List
            itemLayout="horizontal"
            dataSource={existingDocuments}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button 
                    key="view" 
                    icon={<EyeOutlined />} 
                    size="small"
                    onClick={() => handleViewDocument(item.id)}
                  >
                    View
                  </Button>,
                  <Button 
                    key="delete" 
                    icon={<DeleteOutlined />} 
                    danger 
                    size="small"
                    onClick={() => handleDeleteDocument(item.id)}
                  >
                    Delete
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileOutlined style={{ fontSize: 24 }} />}
                  title={item.fileName}
                  description={`Uploaded: ${new Date(item.uploadDate).toLocaleString()}`}
                />
              </List.Item>
            )}
          />
        </div>
      )}
      
      {/* Show existing attachments from valueData with replace option */}
      {showExistingAttachments && existingAttachments.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Divider />
          <Title level={5}>
            <FileOutlined style={{ marginRight: 8 }} />
            Previously Uploaded Documents
          </Title>
          <Alert
            message="Document Review"
            description="These documents were uploaded with your original application. You can view them or replace them if needed."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <List
            itemLayout="horizontal"
            dataSource={existingAttachments.filter((att: any) => !att.isReplaced)}
            renderItem={(item: any) => {
              const isBeingReplaced = replacingDocument === (item.role || item.id);
              const wasReplaced = replacedDocuments.has(item.role || item.id);
              
              return (
                <List.Item
                  actions={[
                    <Tooltip key="view" title="View Document">
                      <Button 
                        icon={<EyeOutlined />} 
                        size="small"
                        onClick={() => handleViewDocument(item.id)}
                      >
                        View
                      </Button>
                    </Tooltip>,
                    allowReplace && !wasReplaced && (
                      isBeingReplaced ? (
                        <Button 
                          key="cancel" 
                          size="small"
                          onClick={handleCancelReplace}
                        >
                          Cancel
                        </Button>
                      ) : (
                        <Tooltip key="replace" title="Replace this document with a new one">
                          <Button 
                            icon={<SwapOutlined />} 
                            size="small"
                            type="primary"
                            ghost
                            onClick={() => handleReplaceDocument(item)}
                          >
                            Replace
                          </Button>
                        </Tooltip>
                      )
                    )
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={
                      wasReplaced ? (
                        <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                      ) : isBeingReplaced ? (
                        <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />
                      ) : (
                        <FileOutlined style={{ fontSize: 24 }} />
                      )
                    }
                    title={
                      <Space>
                        <span>{item.label || item.fileName || item.role || 'Document'}</span>
                        {wasReplaced && <Tag color="success">Replaced</Tag>}
                        {isBeingReplaced && <Tag color="warning">Replacing...</Tag>}
                      </Space>
                    }
                    description={
                      <div>
                        <div>Role: {item.role || 'N/A'}</div>
                        {item.uploadDate && <div>Uploaded: {new Date(item.uploadDate).toLocaleString()}</div>}
                        {isBeingReplaced && (
                          <div style={{ marginTop: 8 }}>
                            <Upload {...uploadProps}>
                              <Button icon={<UploadOutlined />} loading={uploading} type="primary">
                                Select New File
                              </Button>
                            </Upload>
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      )}
    </Card>
  );
};

export default DocumentUploadView;
